import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { API } from '../config';
import { mediaUrl, useAuth } from '../auth';
import { IconSpinner, IconRefresh, IconDownload } from '../components/Icons';
import ExportComposer from '../components/export/ExportComposer';
import { createPortal } from 'react-dom';

const fmt = (n) => (n != null ? Number(n).toLocaleString('it-IT') : '—');
const fmtDate = (s) =>
  s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export default function ExportPage() {
  const location = useLocation();
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerInitial, setComposerInitial] = useState(null);
  const [dlLotto, setDlLotto] = useState(null);
  const [dlModalita, setDlModalita] = useState('unico');
  const [dlEsito, setDlEsito] = useState(false);
  const [dlRegione, setDlRegione] = useState(false);
  const [dlProvincia, setDlProvincia] = useState(false);
  const { can } = useAuth();
  const [dlCopertura, setDlCopertura] = useState(false);
  const [dlVodafone, setDlVodafone] = useState(false);
  const [dlLucchetto, setDlLucchetto] = useState(false);
  const [delLotto, setDelLotto] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [lotti, setLotti] = useState([]);
  const [totalExported, setTotalExported] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 20;
  const [loadingHistory, setLoadingHistory] = useState(false);
  const searchTimer = useRef(null);

  const fetchHistory = useCallback((p = page, q = search) => {
    setLoadingHistory(true);
    axios.get(`${API}/export/lotti`, { params: { page: p, limit: LIMIT, q } })
      .then((r) => {
        setLotti(r.data?.items || []);
        setHistoryTotal(r.data?.total || 0);
        setTotalExported(r.data?.total_exported || 0);
      })
      .catch(() => toast.error('Errore nel caricamento dello storico'))
      .finally(() => setLoadingHistory(false));
  }, [page, search]);

  useEffect(() => { fetchHistory(page, search); }, [fetchHistory, page, search]);

  // handoff dalla Dashboard: apre il compositore con le aree selezionate
  useEffect(() => {
    if (location.state?.exportSel) { setComposerInitial(location.state.exportSel); setComposerOpen(true); }
  }, [location.state]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q); setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchHistory(1, q), 350);
  };

  const directDownload = (lotto) => {
    window.open(mediaUrl(`/export/lotti/${lotto.id}/download?modalita=unico`), '_blank');
    setTimeout(() => fetchHistory(page, search), 1500);
  };
  const openDownload = (lotto) => {
    // una sola provincia = niente da suddividere -> scarica subito (file unico)
    const provEff = lotto?.province_effettive;
    if (Array.isArray(provEff) && provEff.length <= 1) { directDownload(lotto); return; }
    setDlLotto(lotto); setDlModalita('unico'); setDlEsito(false); setDlRegione(false); setDlProvincia(false); setDlCopertura(false); setDlVodafone(false); setDlLucchetto(false);
  };
  const doDownload = () => {
    if (!dlLotto) return;
    const dims = [];
    if (dlModalita !== 'unico') { if (dlEsito) dims.push('esito'); if (dlRegione) dims.push('regione'); if (dlProvincia) dims.push('provincia'); }
    if (dlModalita !== 'unico' && dims.length === 0) { toast.error('Scegli almeno una suddivisione (esito/regione/provincia)'); return; }
    const params = new URLSearchParams({ modalita: dlModalita });
    if (dims.length) params.set('raggruppa', dims.join(','));
    if (dlCopertura) params.set('copertura', '1');
    if (dlVodafone) params.set('vodafone', '1');
    if (dlLucchetto) params.set('lucchetto', '1');
    window.open(mediaUrl(`/export/lotti/${dlLotto.id}/download?${params}`), '_blank');
    setDlLotto(null);
    // il download marca i numeri come lavorati: aggiorna lo storico poco dopo
    setTimeout(() => fetchHistory(page, search), 1500);
  };

  const doDelete = async () => {
    if (!delLotto) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/export/lotti/${delLotto.id}`);
      const rip = r.data?.ripristinati || 0;
      toast.success(rip ? `Lotto eliminato · ${rip} numeri rimessi a disposizione` : 'Lotto eliminato');
      setDelLotto(null);
      fetchHistory(page, search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eliminazione non riuscita');
    } finally {
      setDeleting(false);
    }
  };

  const apriNuovo = () => { setComposerInitial(null); setComposerOpen(true); };
  const totalPages = Math.max(1, Math.ceil(historyTotal / LIMIT));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--text)' }}>Export — Download</h1>
        <button className="btn btn-primary" onClick={apriNuovo}><IconDownload /> Nuovo export</button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Storico lotti</h2>
          {totalExported > 0 && (
            <span style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--surface-2)', borderRadius: 6, padding: '3px 10px' }}>
              Totale nei lotti: <strong>{fmt(totalExported)}</strong> numeri
            </span>
          )}
        </div>

        <div className="toolbar">
          <input type="text" placeholder="Cerca per nota o filename…" value={search} onChange={handleSearchChange} style={{ minWidth: 240 }} />
          <button className="btn btn-secondary" onClick={() => fetchHistory(page, search)} disabled={loadingHistory}>
            {loadingHistory ? <IconSpinner /> : <IconRefresh />} Aggiorna
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>#</th><th>Data</th><th>Numeri</th><th>Stato</th><th>Filtri</th><th>Note</th><th>Download</th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--faint)' }}><IconSpinner /> Caricamento…</td></tr>
              ) : lotti.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--faint)' }}>Nessun lotto. Crea un nuovo export.</td></tr>
              ) : (
                lotti.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 700, color: '#0B5794' }}>#{l.numero_lotto}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(l.totale_numeri)}</td>
                    <td>
                      {(l.scaricato || (l.download_count || 0) > 0) ? (
                        <span className="lotto-stato ok"
                          title={l.last_download_at ? `Ultimo download: ${fmtDate(l.last_download_at)} · ${l.download_count || 0}×` : ''}>
                          ✓ Scaricato{l.totale_consegnati ? ` · ${fmt(l.totale_consegnati)} lavorati` : ''}
                        </span>
                      ) : (
                        <span className="lotto-stato wait">● Mai scaricato</span>
                      )}
                    </td>
                    <td><FiltriBadges lotto={l} /></td>
                    <td style={{ maxWidth: 220, wordBreak: 'break-word', color: 'var(--muted)' }}>
                      {l.nota || <span style={{ color: 'var(--faint)' }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button className="btn btn-primary" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => openDownload(l)}>
                          <IconDownload /> Scarica
                        </button>
                        {!(l.scaricato || (l.download_count || 0) > 0) && (
                          <button className="btn btn-secondary" title="Elimina lotto (mai scaricato)"
                            style={{ padding: '5px 10px', fontSize: 12, color: 'var(--err)', fontWeight: 700 }}
                            onClick={() => setDelLotto(l)}>
                            Elimina
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            <span className="info">Pag. {page} / {totalPages} ({fmt(historyTotal)} lotti)</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        )}
      </div>

      {/* Wizard download */}
      {dlLotto && (
        <div className="ob-overlay" onClick={() => setDlLotto(null)}>
          <div className="dl-modal" onClick={e => e.stopPropagation()}>
            <div className="ob-head">
              <span>Scarica lotto #{dlLotto.numero_lotto}</span>
              <button className="ob-x" onClick={() => setDlLotto(null)}>✕</button>
            </div>
            <div className="dl-body">
              <div className="dl-label">Come vuoi il file?</div>
              <div className="dl-opts">
                {[
                  { v: 'unico', t: 'File unico', d: 'Tutti i numeri in un solo foglio' },
                  { v: 'fogli', t: 'Fogli distinti', d: 'Un foglio per gruppo, stesso file' },
                  { v: 'file_distinti', t: 'File distinti (.zip)', d: 'Un file per gruppo, in uno zip' },
                ].map(o => (
                  <button key={o.v} className={`dl-opt${dlModalita === o.v ? ' on' : ''}`} onClick={() => setDlModalita(o.v)}>
                    <b>{o.t}</b><span>{o.d}</span>
                  </button>
                ))}
              </div>
              <div className={`dl-split${dlModalita === 'unico' ? ' disabled' : ''}`}>
                <div className="dl-label">Suddividi per</div>
                <label><input type="checkbox" disabled={dlModalita === 'unico'} checked={dlEsito} onChange={e => setDlEsito(e.target.checked)} /> Esito</label>
                <label><input type="checkbox" disabled={dlModalita === 'unico'} checked={dlRegione} onChange={e => setDlRegione(e.target.checked)} /> Regione</label>
                <label><input type="checkbox" disabled={dlModalita === 'unico'} checked={dlProvincia} onChange={e => setDlProvincia(e.target.checked)} /> Provincia</label>
              </div>
              {(can('agente_copertura') || can('gea') || can('ssmate')) && (
                <div className="dl-split" style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6, width: '100%' }}>Colonne extra</div>
                  {can('agente_copertura') && (
                    <label><input type="checkbox" checked={dlCopertura} onChange={e => setDlCopertura(e.target.checked)} /> Includi copertura</label>
                  )}
                  {can('gea') && (
                    <label><input type="checkbox" checked={dlVodafone} onChange={e => setDlVodafone(e.target.checked)} /> Includi Vodafone</label>
                  )}
                  {can('ssmate') && (
                    <label><input type="checkbox" checked={dlLucchetto} onChange={e => setDlLucchetto(e.target.checked)} /> Includi lucchetto</label>
                  )}
                </div>
              )}
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 14 }} onClick={doDownload}>
                <IconDownload /> Scarica
              </button>
            </div>
          </div>
        </div>
      )}

      {delLotto && createPortal(
        <div className="confirm-overlay" onClick={() => !deleting && setDelLotto(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-title">Eliminare il lotto #{delLotto.numero_lotto}?</div>
            <div className="confirm-text">
              Il lotto non e' mai stato scaricato: i suoi numeri restano disponibili per l'export.
              L'operazione e' definitiva (record e file).
            </div>
            <div className="confirm-btns">
              <button className="btn btn-danger" disabled={deleting} onClick={doDelete}>
                {deleting ? '…' : 'Elimina'}
              </button>
              <button className="btn btn-secondary" disabled={deleting} onClick={() => setDelLotto(null)}>
                Annulla
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Compositore in finestra dialogo */}
      {composerOpen && (
        <div className="ob-overlay" onClick={() => setComposerOpen(false)}>
          <div className="xc-modal" onClick={e => e.stopPropagation()}>
            <div className="ob-head">
              <span>Nuovo export</span>
              <button className="ob-x" onClick={() => setComposerOpen(false)}>✕</button>
            </div>
            <div className="xc-modal-body">
              <ExportComposer initial={composerInitial} onExported={() => { setPage(1); fetchHistory(1, search); setComposerOpen(false); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FiltriBadges({ lotto }) {
  const filtri = (lotto && lotto.filtri) || {};
  if (typeof filtri !== 'object') return <span style={{ color: 'var(--faint)', fontSize: 12 }}>Tutti</span>;
  const out = [];
  // aree EFFETTIVE (presenti nel lotto) se disponibili, altrimenti quelle selezionate
  const nReg = lotto?.regioni_effettive ? lotto.regioni_effettive.length : (Array.isArray(filtri.regioni) ? filtri.regioni.length : 0);
  const nProv = lotto?.province_effettive ? lotto.province_effettive.length : (Array.isArray(filtri.province) ? filtri.province.length : 0);
  if (nReg) out.push(['regioni', nReg]);
  if (nProv) out.push(['province', nProv]);
  Object.entries(filtri).forEach(([k, v]) => {
    if (['regioni', 'province', 'raggruppa', 'formato'].includes(k)) return;
    if (v != null && v !== '') out.push([k, String(v)]);
  });
  if (out.length === 0) return <span style={{ color: 'var(--faint)', fontSize: 12 }}>Tutti</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {out.map(([k, v]) => (
        <span key={k} style={{ background: 'var(--surface-2)', color: 'var(--info)', border: '1px solid var(--surface-2)', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
          {k}: {v}
        </span>
      ))}
    </div>
  );
}
